import React from 'react';
import { SocialIcon } from '../components/SocialIcon';
import { directContactInfo, socialLinks } from '../data/contact';

const ContactPage: React.FC = () => {
  return (
    <div>
      <section className='py-16 text-center bg-muted'>
        <div className='container mx-auto px-6'>
          <p className='text-sm font-bold tracking-widest text-secondary uppercase mb-3 opacity-0 animate-fade-in-up'>
            Say Hello
          </p>
          <h1 className='text-4xl font-bold text-foreground font-heading opacity-0 animate-fade-in-up'>
            Contact & <span className='text-primary'>Support</span>
          </h1>
          <p
            className='text-lg text-muted-foreground mt-2 opacity-0 animate-fade-in-up'
            style={{ animationDelay: '0.2s' }}
          >
            We'd love to hear from you. Get in touch with our team.
          </p>
        </div>
      </section>

      <section className='py-20'>
        <div className='container mx-auto px-6'>
          <div className='grid md:grid-cols-5 gap-12'>
            {/* Left Column: Contact Form */}
            <div
              className='md:col-span-3 bg-card p-8 rounded-lg border border-border shadow-sm opacity-0 animate-slide-in-left'
              style={{ animationDelay: '0.3s' }}
            >
              <h2 className='text-2xl font-bold text-foreground mb-6 font-heading'>Send us a Message</h2>
              <form action='#' method='POST' className='space-y-6'>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-6'>
                  <div>
                    <label htmlFor='first-name' className='block text-sm font-medium text-muted-foreground mb-1'>
                      First Name
                    </label>
                    <input
                      type='text'
                      name='first-name'
                      id='first-name'
                      autoComplete='given-name'
                      className='w-full bg-background border border-input rounded-md px-3 py-2 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow'
                    />
                  </div>
                  <div>
                    <label htmlFor='last-name' className='block text-sm font-medium text-muted-foreground mb-1'>
                      Last Name
                    </label>
                    <input
                      type='text'
                      name='last-name'
                      id='last-name'
                      autoComplete='family-name'
                      className='w-full bg-background border border-input rounded-md px-3 py-2 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow'
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor='email' className='block text-sm font-medium text-muted-foreground mb-1'>
                    Email
                  </label>
                  <input
                    type='email'
                    name='email'
                    id='email'
                    autoComplete='email'
                    required
                    className='w-full bg-background border border-input rounded-md px-3 py-2 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow'
                  />
                </div>
                <div>
                  <label htmlFor='phone' className='block text-sm font-medium text-muted-foreground mb-1'>
                    Phone <span className='text-xs'>(Optional)</span>
                  </label>
                  <input
                    type='tel'
                    name='phone'
                    id='phone'
                    autoComplete='tel'
                    className='w-full bg-background border border-input rounded-md px-3 py-2 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow'
                  />
                </div>
                <div>
                  <label htmlFor='message' className='block text-sm font-medium text-muted-foreground mb-1'>
                    Message
                  </label>
                  <textarea
                    name='message'
                    id='message'
                    rows={5}
                    required
                    className='w-full bg-background border border-input rounded-md px-3 py-2 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow'
                  ></textarea>
                </div>
                <div>
                  <button
                    type='submit'
                    className='w-full bg-primary text-primary-foreground font-semibold px-6 py-3 rounded-md hover:bg-primary/90 transition-all duration-300 transform hover:scale-[1.02]'
                  >
                    Send Message
                  </button>
                </div>
              </form>
            </div>

            {/* Right Column: Contact Info */}
            <div className='md:col-span-2 opacity-0 animate-slide-in-right' style={{ animationDelay: '0.4s' }}>
              <div className='bg-card p-8 rounded-lg border border-border h-full shadow-sm'>
                <h2 className='text-2xl font-bold text-foreground mb-6 font-heading'>Direct Contact</h2>
                <p className='text-muted-foreground mb-8'>
                  For direct inquiries, partnerships, or support, feel free to reach out to us through the channels
                  below.
                </p>
                <ul className='space-y-6'>
                  {directContactInfo.map((item) => (
                    <li key={item.name} className='flex items-start space-x-4 group'>
                      <div className='shrink-0 text-secondary mt-1 group-hover:scale-110 transition-transform'>
                        <item.icon className='h-6 w-6' />
                      </div>
                      <div>
                        <h4 className='font-semibold text-foreground'>{item.name}</h4>
                        {item.href ? (
                          <a href={item.href} className='text-muted-foreground hover:text-primary transition-colors'>
                            {item.value}
                          </a>
                        ) : (
                          <p className='text-muted-foreground'>{item.value}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                <div className='mt-8 pt-6 border-t border-border'>
                  <h4 className='font-semibold text-foreground mb-4'>Follow Us</h4>
                  <div className='flex space-x-4'>
                    {socialLinks.map((social) => (
                      <SocialIcon key={social.name} ariaLabel={`Follow us on ${social.name}`} href={social.href}>
                        <social.icon className='w-6 h-6 transform hover:scale-110 transition-transform' />
                      </SocialIcon>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
